import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { formatCallDuration, leadAnswerKeys, leadAnswerLabels } from '@brimo/shared';
import { Button } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { promoteListContact, type DialerContact, type DialerCurrent, type DialerTimelineRow } from '../api';
import { Eyebrow, FactRow } from '../ui';
import styles from './session.module.css';

const TYPE_LABELS: Record<string, string> = {
  call: 'Anruf',
  email: 'E-Mail',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  note: 'Notiz',
  appointment: 'Termin',
  task: 'Aufgabe',
  document: 'Dokument',
  portal: 'Portal',
  system: 'System',
  self_disclosure: 'Selbstauskunft',
  reservation: 'Reservierung',
  meeting: 'Beratung',
  chatbot: 'Chatbot',
};

const DIRECTION_LABELS: Record<DialerTimelineRow['direction'], string | null> = {
  outbound: 'ausgehend',
  inbound: 'eingehend',
  internal: null,
};

function channelOf(row: { type: string; direction: DialerTimelineRow['direction'] }): string {
  const type = TYPE_LABELS[row.type] ?? row.type;
  const direction = DIRECTION_LABELS[row.direction];
  return direction ? `${type} · ${direction}` : type;
}

/** The running call on top of „Verlauf" („gerade · Anruf · ausgehend · läuft · 4:12"). */
export interface LiveRow {
  status: string;
  text: string;
}

/** Left column of 08: the contact card with its properties and „Angaben aus der Eintragung". */
export function ContactColumn({ current }: { current: DialerCurrent }) {
  const contact = current.contact;
  const properties: Array<[string, string | null]> = [
    ['E-Mail', contact.email],
    ['Lifecycle', contact.lifecycle.label],
    ['Liste', contact.listName],
    ['Quelle', contact.source],
    ['Betreuer', contact.advisor],
    ['Objekt', contact.property],
    ['Letzter Kontakt', contact.lastContact],
  ];
  const answers = contact.leadAnswers;
  return (
    <div className={styles.column}>
      <section className={styles.contactCard} aria-label="Kontakt">
        <Link to={`/contacts/${contact.id}`} className={styles.contactName}>
          {contact.name}
        </Link>
        {contact.phoneDisplay ? <div className={styles.contactPhone}>{contact.phoneDisplay}</div> : null}
        <div className={styles.pills}>
          <span className={styles.pillAccent}>{contact.lifecycle.label}</span>
          {contact.attempt > 0 ? <span className={styles.pillQuiet}>Versuch {contact.attempt}</span> : null}
          {contact.dnc ? <span className={styles.pillDanger}>DNC</span> : null}
        </div>
        <div className={styles.properties}>
          {properties
            .filter((entry): entry is [string, string] => entry[1] !== null && entry[1] !== '')
            .map(([label, value]) => (
              <FactRow key={label} label={label} value={value} />
            ))}
        </div>
      </section>
      {answers ? (
        <section className={styles.contactCard} aria-label="Angaben aus der Eintragung">
          <Eyebrow>Angaben aus der Eintragung</Eyebrow>
          <div className={styles.answers}>
            {leadAnswerKeys
              .filter((key) => answers[key])
              .map((key) => (
                <FactRow key={key} label={leadAnswerLabels[key]} value={answers[key]} />
              ))}
          </div>
          {contact.leadLine ? <div className={styles.leadLine}>{contact.leadLine}</div> : null}
        </section>
      ) : null}
      {contact.importInfo ? <ImportInfoCard contactId={contact.id} info={contact.importInfo} /> : null}
    </div>
  );
}

/**
 * „Aus der Importliste" (D-241): the extra columns of the import row and, for a staged contact, „Als Kontakt
 * übernehmen" — a reached result promotes it anyway.
 */
function ImportInfoCard({
  contactId,
  info,
}: {
  contactId: string;
  info: NonNullable<DialerContact['importInfo']>;
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function promote() {
    setPending(true);
    setError(null);
    try {
      await promoteListContact(info.listId, contactId);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className={styles.contactCard} aria-label="Aus der Importliste">
      <Eyebrow>Aus der Importliste</Eyebrow>
      {info.extra.length > 0 ? (
        <div className={styles.answers}>
          {info.extra.map((entry, index) => (
            <FactRow key={`${entry.label}-${index}`} label={entry.label} value={entry.value} />
          ))}
        </div>
      ) : null}
      <div className={styles.leadLine}>
        {info.staged
          ? `Importkontakt aus „${info.listName}“ — wird zum Kontakt, sobald Sie ihn erreichen.`
          : `Aus „${info.listName}“ · bereits Kontakt`}
      </div>
      {error ? <div className={styles.leadLine}>{error}</div> : null}
      {info.canPromote ? (
        <Button variant="secondary" size="small" loading={pending} onClick={() => void promote()}>
          Als Kontakt übernehmen
        </Button>
      ) : null}
    </section>
  );
}

/** Middle column of 08: „Verlauf" with the running call on top. */
export function TimelineCard({ rows, live }: { rows: DialerTimelineRow[]; live: LiveRow | null }) {
  const count = rows.length + (live ? 1 : 0);
  return (
    <section className={styles.card} aria-labelledby="dialer-timeline-title">
      <div className={styles.cardHead}>
        <Eyebrow>
          <span id="dialer-timeline-title">Verlauf</span>
        </Eyebrow>
        <span className={styles.cardHeadNote}>
          {count} {count === 1 ? 'Ereignis' : 'Ereignisse'}
        </span>
      </div>
      <ol className={styles.timeline}>
        {live ? (
          <TimelineItem when="gerade" channel="Anruf · ausgehend" status={live.status}>
            <p className={styles.timelineText}>{live.text}</p>
          </TimelineItem>
        ) : null}
        {rows.map((row) => (
          <TimelineItem
            key={row.id}
            when={row.whenLabel}
            channel={channelOf(row)}
            status={row.statusLabel ?? '—'}
          >
            <p className={styles.timelineText}>{row.title}</p>
            {row.text ? <p className={styles.timelineSub}>{row.text}</p> : null}
          </TimelineItem>
        ))}
        {count === 0 ? <li className={styles.slotsNote}>Noch keine Ereignisse.</li> : null}
      </ol>
    </section>
  );
}

function TimelineItem({
  when,
  channel,
  status,
  children,
}: {
  when: string;
  channel: string;
  status: string;
  children: ReactNode;
}) {
  return (
    <li className={styles.timelineItem}>
      <div className={styles.timelineWhen}>{when}</div>
      <div className={styles.timelineBox}>
        <div className={styles.timelineHead}>
          <span className={styles.timelineChannel}>{channel}</span>
          <span className={styles.timelineStatus}>{status}</span>
        </div>
        {children}
      </div>
    </li>
  );
}

/** Navy KI-Briefing card (only while the server delivers a briefing). */
export function BriefingCard({ briefing }: { briefing: { text: string; tags: string[] } }) {
  return (
    <section className={styles.briefing} aria-label="KI-Briefing">
      <Eyebrow tone="inverse">KI-Briefing</Eyebrow>
      <p className={styles.briefingText}>{briefing.text}</p>
      {briefing.tags.length > 0 ? (
        <div className={styles.briefingTags}>
          {briefing.tags.map((tag) => (
            <span key={tag} className={styles.briefingTag}>
              {tag}
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** „Notiz" of the call (goes with the result); the recording line only while recording is switched on. */
export function NoteCard({
  note,
  onNote,
  recording,
}: {
  note: string;
  onNote: (note: string) => void;
  recording: boolean;
}) {
  return (
    <section className={styles.noteCard} aria-label="Notiz">
      <label className={styles.noteEyebrow} htmlFor="dialer-call-note">
        Notiz
      </label>
      <textarea
        id="dialer-call-note"
        className={styles.noteField}
        rows={3}
        value={note}
        onChange={(event) => onNote(event.target.value)}
      />
      {recording ? <div className={styles.recording}>Aufnahme läuft</div> : null}
    </section>
  );
}

/** „läuft · 4:12" / „wählt …" for the live timeline row. */
export function liveStatus(phase: 'dialing' | 'connected', seconds: number | null): string {
  return phase === 'connected' ? `läuft · ${formatCallDuration(seconds ?? 0)}` : 'wählt …';
}
